/**
 * WCS bounds — the dedicated per-entity axis-aligned WCS bounds helper (T1 arm (c)).
 *
 * `computeEntityWcsBounds(entity, ctx?)` is the "REAL FIX" `captureSnapshot.ts`'s module
 * header names as the known follow-up to the deferred `entityBounds` facet: the prior only
 * bounds source (`viewportMembership.ts::entityModelBounds`) is a viewport-clip MEMBERSHIP
 * window test, not an accurate WCS bounds source — it is 2D-only (no OCS/extrusion, no Z),
 * reports Text/MText as insertion-point-only (no glyph extent), tiles a MINSERT INSERT as one
 * cell only, and silently drops several legal kinds from its own `default` case. This module
 * fixes each gap directly, for every one of the 17 top-level `DrawingEntity` kinds:
 *   - OCS→WCS via the extrusion (group 210/220/230) — `./wcsBoundsMath.ts`'s Arbitrary Axis
 *     Algorithm, applied uniformly through `aabb3FromLocalPoints` (identity for the
 *     overwhelmingly common default-extrusion case, a real 3D rotation otherwise).
 *   - Z — every point carries a real Z (from a 3D field, an `elevation` scalar, or 0 when the
 *     kind genuinely has none); `thickness` (DXF group 39) additionally extrudes the local
 *     point set along OCS-Z by the thickness value.
 *   - Text / MText glyph extent — `./wcsBoundsText.ts` (justification-aware box, not a bare
 *     insertion point).
 *   - Block / MINSERT — `./wcsBoundsBlock.ts` (recursive block-definition resolution + MINSERT
 *     grid tiling, kept in its own module — this file's own size budget, mirrors the
 *     `cad-core/bounds.ts` ↔ `blockGeometry.ts` split for the identical reason).
 *   - Per-kind LOCAL point extraction (the actual per-type math) lives in `./wcsBoundsLeaf.ts`
 *     — shared by THIS dispatcher (top-level entities) and `./wcsBoundsBlock.ts` (block-
 *     definition children), avoiding an import cycle between the two (see that module's header).
 *
 * `null` is an HONEST "cannot compute" (never a fabricated box) — e.g. a spline with zero
 * control/fit points, or an unresolvable/missing/cyclic block INSERT with no `blockDefinitions`
 * context supplied. `captureSnapshot.ts`'s lazy `entityBounds` getter OMITS the field on `null`
 * (the seam's documented `boundsKnown=false` marker — never a placeholder box).
 *
 * KNOWN RESIDUAL APPROXIMATIONS (documented per CLAUDE.md ПРИОРИТЕТ #1 — "known incomplete
 * subsystem with a plan", not a silent shortcut):
 *   - Text/MText WIDTH is a `height × widthFactor × 0.6` estimate absent a resolved DXF width
 *     field (no font metrics at the model layer) — see `./wcsBoundsText.ts`'s header.
 *   - Spline bounds use the CONTROL-POINT CONVEX HULL (a real, non-hack, provably-conservative
 *     bound — a B-spline curve never leaves its control polygon's convex hull), not the tight
 *     analytic curve extent.
 *   - A tilted-OCS (non-default extrusion) Arc adds a dense curve tessellation (see
 *     `./wcsBoundsMath.ts::tessellateArc2D`) alongside the exact in-plane critical points, so
 *     the reported WCS box stays conservative under rotation — matching `ezdxf.bbox`'s own
 *     flattening-based strategy for curved entities, not a closed-form 3D-rotated-extremum
 *     solve. Ellipse (@codex T1 arm(c) round-22 P2 finding — F1) instead computes its critical
 *     points DIRECTLY in full 3D (`./wcsBoundsMath.ts::ellipseExtentPoints3D`/
 *     `tessellateEllipse3D`, from the entity's real 3D `majorAxisEndpoint` vector and the real
 *     `extrusion × majorAxis` minor-axis direction — matching the render tessellator's own
 *     formula exactly), rather than a 2D-plane-local extent plus a separate Z pad — see
 *     `wcsBoundsLeaf.ts::ellipseLocal`'s own doc for the full history.
 *   - `ArrayRect`'s bounds are the PARAMETRIC GRID extent (base point + row/column/level total
 *     span along the resolved axis directions) — NOT the union of the actual replicated item
 *     geometry (which needs a whole-document entity-id lookup this per-entity function does not
 *     take). Mirrors the SAME documented scope the `geometry` facet (`captureEntityGeometry.ts`)
 *     already uses for this kind ("the defining GRID … NOT the agent grid").
 *   - A block INSERT's per-instance ATTRIB text uses its block-DEFINITION ATTDEF template
 *     position (via the recursive block-def walk) when the instance carries no
 *     `overridePosition`; when `overridePosition` IS set it is a full justified box anchored at
 *     that WORLD-absolute point (no further transform beyond MINSERT grid tiling — the schema's
 *     own documented invariant), TILED across every MINSERT cell. An orphan ATTRIB (tag matches
 *     no ATTDEF) uses the renderer's own PLAIN-style fallback rather than being dropped. See
 *     `wcsBoundsBlock.ts::resolveAttributeContribution`/`tileAcrossMinsertCells` for the full,
 *     non-approximated logic (@codex T1 arm(c) rounds 6+10 closed the prior approximation gaps
 *     this bullet used to describe).
 *   - A block-child MULTILEADER's own BLOCK content (contentType 1) is not resolved (only its
 *     leader/anchor/MText-content geometry contributes) — a TOP-LEVEL Multileader's block
 *     content IS resolved (below). Documented gap, narrow (nested MLEADER-with-block-content
 *     inside a block definition is a rare authoring pattern).
 *   - An annotative TEXT/MTEXT's `entityBounds` covers EVERY `annotativeContexts[]` representation
 *     simultaneously, not just one (@codex T1 arm(c) round-23 P2 finding — F8, closing the round-22
 *     "documented limitation" above: Codex re-flagged the doc-only resolution, so a comment alone
 *     did not close the real under-report). The active CANNOSCALE is inherently VIEWPORT-scoped —
 *     multiple layout viewports can show the same annotative TEXT/MTEXT at different active scales
 *     simultaneously — and this facet is a per-entity, viewport-agnostic document-global snapshot,
 *     so there is no single well-defined "the" active context to resolve against (unlike the
 *     DIMSTYLE gap, round-22 finding 2, a simple document-level table lookup). Rather than guessing
 *     one, `textLocal`/`mtextLocal` UNION the master box with every context's own resolved box —
 *     this module's own established "provably conservative, never under-report" convention (the
 *     same reasoning `dense` curve tessellation already applies for Circle/Arc/Ellipse). See
 *     `wcsBoundsLeaf.ts::textLocal`/`mtextLocal`'s own docs for the full derivation.
 */

import type { DrawingEntity } from "./model";
import { aabb3FromLocalPoints, mapBlockAttributeReferences, splineLeaderControlPoints, unionAabb3, type Wcs3, type WcsAabb3 } from "./wcsBoundsMath";
import {
  arcLocal,
  arrayRectLocal,
  centeredTextBoxLocal,
  circleLocal,
  dimensionLocal,
  ellipseLocal,
  hatchLocal,
  lineLocal,
  lwpolylineLocal,
  multileaderDoglegEnd,
  multileaderLandingEnd,
  multileaderNodeLastVertex,
  mtextLocal,
  pointLocal,
  radToDeg,
  revisionCloudLocal,
  splineLocal,
  textLocal,
  viewportLocal,
  wipeoutLocal,
  heavyPolylineLocal,
  oleFrameLocal,
} from "./wcsBoundsLeaf";
import { blockInsertWcsBounds, tableWcsBounds, resolveDimensionStyleFromContext, safeRecordLookup, type BlockLookupContext } from "./wcsBoundsBlock";

export {
  constantAttdefLocalCorners,
  createBlockBoundsCache,
  resolveAttributeContribution,
  safeRecordLookup,
  type AttributeLike,
  type BlockBoundsCache,
  type BlockLookupContext,
} from "./wcsBoundsBlock";
export type { WcsAabb3 } from "./wcsBoundsMath";

/**
 * @codex T1 arm(c) round-25 P2 finding (Fix 1) — per-`annotativeContexts[]`-representation
 * arrowhead pad + dogleg/landing-gap endpoint, mirroring the MASTER walk's own round-21 F7
 * arrowhead pad and round-12 dogleg-end union (both in {@link multileaderWcsBounds} above), just
 * substituting the CONTEXT's own `arrowheadSize`/`doglegLength`/`landingGap` in place of the
 * entity's. Confirmed live: `apps/web/…/adapters/typed/multileaderFromEntity.ts` resolves
 * `raw.arrowheadSize = annoCtx?.arrowheadSize ?? entity.arrowheadSize` (and `doglegLength`/
 * `landingGap` identically) from the ONE matched context (`pickAnnotativeContext`) — so whichever
 * context is active at a given viewport's CANNOSCALE, the renderer draws the arrow/dogleg/landing
 * at THAT context's own sizes, not the master's. This facet is viewport-agnostic (no single "the"
 * active context — same reasoning the round-24 text-box context union already documents), so it
 * unions every context's own footprint rather than guessing one. The base leader-line VERTICES
 * (walked once in the master loop above) do not vary per context — `mleaderContextDataSchema`
 * carries no vertex/geometry override field — only the arrow/dogleg/landing SIZES do.
 */
function multileaderContextLeaderPad(
  entity: Extract<DrawingEntity, { type: "Multileader" }>,
  arrowheadSize: number,
  doglegLength: number,
  landingGap: number,
): Wcs3[] {
  const pts: Wcs3[] = [];
  const arrowVisible = entity.leaderLineType !== 0;
  const doglegEntity = { ...entity, doglegLength };
  for (const root of entity.leaderRoots) {
    for (const node of root.nodes) {
      for (const line of node.lines) {
        const resolvedArrow = line.arrowheadBlock || entity.arrowheadBlock;
        if (arrowVisible && line.vertices.length > 0 && resolvedArrow !== "_NONE") {
          const tip = line.vertices[0]!;
          pts.push({ x: tip.x + arrowheadSize, y: tip.y + arrowheadSize, z: tip.z ?? 0 });
          pts.push({ x: tip.x - arrowheadSize, y: tip.y - arrowheadSize, z: tip.z ?? 0 });
        }
      }
      const lastVertex = multileaderNodeLastVertex(entity, node);
      const doglegEnd = multileaderDoglegEnd(doglegEntity, node, lastVertex);
      if (doglegEnd) {
        pts.push({ x: doglegEnd.x, y: doglegEnd.y, z: 0 });
        const landingEnd = multileaderLandingEnd(entity.textLocation, doglegEnd, landingGap);
        pts.push({ x: landingEnd.x, y: landingEnd.y, z: 0 });
      }
    }
  }
  return pts;
}

/**
 * A top-level MULTILEADER's WCS bounds: leader/anchor + (for a text-content MLEADER) an
 * estimated text box UNIONED with (for a BLOCK-content MLEADER, `contentType===1`) the
 * referenced block's real footprint at `textLocation` — the synthetic single-instance INSERT
 * both live renderers use (`scene/renderMultileader.tsx` / `scene3d/dimensions/
 * multileaderToPrimitives.ts`), resolved via `blockInsertWcsBounds` (mirrors cad-core's
 * `multileaderBlockContentBounds`/`makeMleaderContentInsert`).
 */
function multileaderWcsBounds(entity: Extract<DrawingEntity, { type: "Multileader" }>, ctx?: BlockLookupContext): WcsAabb3 | null {
  const pts = [{ x: entity.textLocation.x, y: entity.textLocation.y, z: 0 }];
  if (entity.basePoint) pts.push({ x: entity.basePoint.x, y: entity.basePoint.y, z: 0 });
  // @codex T1 arm(c) round-12 P2 finding: a SPLINE-type (leaderLineType===2) leader line bows
  // OUT past its raw vertices (Catmull-Rom cubic control points — `splineLeaderControlPoints`'s
  // doc), and the DOGLEG segment jogs past the last leader-line vertex
  // (`multileaderDoglegEnd`'s doc) — both are now unioned in, matching cad-core's
  // `multileaderBounds` exactly.
  const isSpline = entity.leaderLineType === 2;
  // @codex T1 arm(c) round-21 P2 finding (F7): a visible leader (`leaderLineType !== 0`) draws an
  // ARROWHEAD PRIMITIVE at the FIRST vertex of every leader LINE (`scene3d/adapters/geom/
  // multileaderGeometry.ts::buildMultileaderGeometry`: `const v0 = verts[0]!; ... if (firstArrow
  // !== "_NONE") primitives.push({ kind: "arrow", tip: v0, size: raw.arrowheadSize, ... })` — the
  // SAME shared builder both the SVG (`renderMultileader.tsx`) and WebGL renderer feed, so this
  // covers both paths). `arrowMarkerMesh.ts`'s unit-size prototypes show the real footprint can
  // extend past that vertex: a standard closed/open/dot triangle's BASE is `HALF_WIDTH=0.5` units
  // PERPENDICULAR to the leader direction at the scaled `arrowheadSize`, and the ARCH-TICK/OBLIQUE
  // tick variants extend up to `0.5` unit (scaled) in EITHER local axis from the tip, including
  // PAST the vertex outward — a conservative square pad of `arrowheadSize` around the first
  // vertex, matching this module's established "conservative, padding-based" bounds-approximation
  // pattern (see e.g. `wcsBoundsLeaf.ts::dimensionLocal`'s own DIMASZ pad), covers every variant's
  // real footprint. A CUSTOM `arrowheadBlock` (a real BLOCK reference, not one of the `_XXX`
  // documented DIMBLK tokens) is confirmed SAFE under the same pad: `arrowMarkerMesh.ts
  // ::dimblkToVariant`'s `default:` case degrades ANY unrecognized block name to `"closed-filled"`
  // — this app's renderer does not yet draw the referenced block's own geometry for a custom
  // arrowhead at all (a documented, pre-existing, separate gap), always scaling the SAME built-in
  // triangle prototype by `arrowheadSize` regardless of the block name, so the `arrowheadSize`-only
  // pad remains accurate for every currently-reachable render outcome.
  const arrowVisible = entity.leaderLineType !== 0;
  for (const root of entity.leaderRoots) {
    for (const node of root.nodes) {
      if (node.lastLeaderPoint) {
        pts.push({ x: node.lastLeaderPoint.x, y: node.lastLeaderPoint.y, z: node.lastLeaderPoint.z });
      }
      for (const line of node.lines) {
        for (const v of line.vertices) pts.push({ x: v.x, y: v.y, z: v.z ?? 0 });
        if (isSpline) {
          for (const c of splineLeaderControlPoints(line.vertices)) pts.push({ x: c.x, y: c.y, z: c.z });
        }
        const resolvedArrow = line.arrowheadBlock || entity.arrowheadBlock;
        if (arrowVisible && line.vertices.length > 0 && resolvedArrow !== "_NONE") {
          const tip = line.vertices[0]!;
          const pad = entity.arrowheadSize;
          pts.push({ x: tip.x + pad, y: tip.y + pad, z: tip.z ?? 0 });
          pts.push({ x: tip.x - pad, y: tip.y - pad, z: tip.z ?? 0 });
        }
      }
      const doglegEnd = multileaderDoglegEnd(entity, node, multileaderNodeLastVertex(entity, node));
      if (doglegEnd) {
        pts.push({ x: doglegEnd.x, y: doglegEnd.y, z: 0 });
        // @codex T1 arm(c) round-25 P2 finding (Fix 1): the landing-gap endpoint past the dogleg —
        // see {@link multileaderLandingEnd}'s own doc.
        const landingEnd = multileaderLandingEnd(entity.textLocation, doglegEnd, entity.landingGap);
        pts.push({ x: landingEnd.x, y: landingEnd.y, z: 0 });
      }
    }
  }
  // @codex T1 arm(c) round-25 P2 finding (Fix 1): union EVERY `annotativeContexts[]`
  // representation's own arrowhead pad + dogleg/landing-gap endpoint — see
  // {@link multileaderContextLeaderPad}'s own doc for the full renderer-resolution derivation.
  // Runs regardless of `contentType` (leader/arrow/dogleg geometry is drawn for every content
  // type, not just text-content).
  for (const context of entity.annotativeContexts ?? []) {
    pts.push(...multileaderContextLeaderPad(entity, context.arrowheadSize, context.doglegLength, context.landingGap));
  }
  let leafPts = pts;
  if (entity.contentType === 2) {
    // @codex T1 arm(c) round-21 P2 finding (F8): an empty `textContents` on a text-content MLEADER
    // is NOT "no text" — the typed renderer substitutes a fallback string BEFORE building the text
    // primitive (`multileaderFromEntity.ts`: `const contents = entity.textContents || \`MLeader
    // ${entity.id}\`;`), so a real (fallback) text box is still drawn and must still be bounded.
    // Matches the renderer's exact fallback string, not just a generic placeholder.
    const contents = entity.textContents || `MLeader ${entity.id}`;
    // @codex T1 arm(c) round-10 P2 finding: honor textWidth/textLineSpacingFactor (see
    // `centeredTextBoxLocal`'s doc — now matches cad-core's `multileaderTextContentCorners`
    // exactly), not just a fixed glyph-aspect estimate.
    leafPts = [
      ...leafPts,
      // GitHub Codex review PR #558 round 2 P2 — `entity.textRotation` is
      // RADIANS (see `radToDeg`'s own doc); `centeredTextBoxLocal`'s
      // `rotationDeg` param needs DEGREES.
      ...centeredTextBoxLocal(entity.textLocation, contents, entity.textHeight, radToDeg(entity.textRotation), entity.textWidth, entity.textLineSpacingFactor),
    ];
    // @codex T1 arm(c) round-24 P2 finding: union EVERY `annotativeContexts[]` MLEADER text-content
    // representation's own box too — mirrors the SAME "provably conservative, never under-report"
    // convention `textLocal`/`mtextLocal` (`wcsBoundsLeaf.ts`, round-23 F8) already establish. The
    // renderer (`apps/web/…/adapters/typed/multileaderFromEntity.ts`) resolves ONE matched context
    // per active CANNOSCALE via `pickAnnotativeContext` and substitutes `textHeight`/`textRotation`
    // from it BEFORE building the text primitive (`annoCtx?.textHeight ?? entity.textHeight`,
    // `annoCtx?.textRotation ?? entity.textRotation`) — `textLocationRaw` and `textLineSpacingFactor`
    // are NEVER context-overridden (the renderer always reads `entity.textLocation`;
    // `mleaderContextDataSchema` — model.ts — carries no location/line-spacing field at all).
    // `textWidth` IS part of the context schema and, like the master's own `entity.textWidth`, feeds
    // this module's `centeredTextBoxLocal` wrap-width estimate (mirrors `cad-core`'s
    // `multileaderTextContentCorners`, this module's own established convention for the field — see
    // that helper's doc). Like the Block/INSERT "blockref" context (round-24, `wcsBoundsBlock.ts`),
    // this facet is per-entity and VIEWPORT-agnostic — the active CANNOSCALE is inherently
    // per-viewport — so there is no single "the" active context to resolve against here either;
    // unioning every context's own box is the same honest, conservative choice.
    for (const context of entity.annotativeContexts ?? []) {
      leafPts = [
        ...leafPts,
        // GitHub Codex review PR #558 round 2 P2 — `context.textRotation`
        // is the SAME radians field as the flat `textRotation` above.
        ...centeredTextBoxLocal(entity.textLocation, contents, context.textHeight, radToDeg(context.textRotation), context.textWidth, entity.textLineSpacingFactor),
      ];
    }
  }
  let box = aabb3FromLocalPoints(leafPts, undefined);

  if (entity.contentType === 1 && entity.blockName) {
    // @codex T1 arm(c) round-18 P2 finding: an unresolved/unresolvable BLOCK-content MLEADER
    // (missing block context, OR a missing/cyclic definition) must NOT silently fall back to the
    // leader/text-only hull — the unresolved block geometry may extend anywhere, so a "known"
    // partial box here is MISLEADING for a window-select/containment consumer. This mirrors the
    // exact "honest unknown over a misleading partial box" policy `blockInsertWcsBounds`'s own
    // doc already establishes for a top-level Block (INSERT) entity — applied consistently here
    // for MLEADER block content.
    if (!ctx?.blockDefinitions) return null;
    const content = blockInsertWcsBounds(
      {
        type: "Block",
        id: "",
        spaceId: "",
        blockName: entity.blockName,
        insertionPoint: { x: entity.textLocation.x, y: entity.textLocation.y, z: 0 },
        scaleX: entity.blockScale,
        scaleY: entity.blockScale,
        // scaleZ is ALWAYS 1, never `entity.blockScale` (@codex T1 arm(c) round-10 P2 finding) —
        // matches BOTH established renderer precedents byte-for-byte: `cad-core/blockGeometry.ts::
        // makeMleaderContentInsert` (`scaleZ: 1`) and the 3D flattener
        // (`scene3d/adapters/geom/multileaderGeometry.ts`: `scale: vec3(blockScale, blockScale,
        // 1)`). `blockScale` is a 2D (X/Y plan-view) MLEADER content scale, not a 3D uniform
        // scale — extruding Z by it would mis-report the content block's Z-extent for any block
        // geometry with real out-of-plane depth.
        scaleZ: 1,
        rotation: entity.blockRotation,
        columnCount: 1,
        rowCount: 1,
        columnSpacing: 0,
        rowSpacing: 0,
        // The identity extrusion — NEVER `entity.blockScaleVector` (@codex T1 arm(c) round-2 P2
        // finding): `blockScaleVector` (AcDbMLeader BODY group 10/20/30) is a per-axis BLOCK
        // SCALE vector, not an OCS plane normal — a syntactically-similar `{x,y,z}` shape but a
        // semantically unrelated field. Feeding it into the Arbitrary Axis Algorithm as an
        // extrusion rotated/skewed the block content's bounds arbitrarily. MULTILEADER carries no
        // genuine extrusion field on this model at all (`multileaderEntitySchema` has none) — both
        // live renderers (`scene/renderMultileader.tsx` / `scene3d/dimensions/
        // multileaderToPrimitives.ts`) place the block content using only `blockScale`/
        // `blockRotation`, never `blockScaleVector` as a plane tilt.
        extrusion: { x: 0, y: 0, z: 1 },
        // @codex T1 arm(c) round-19 P2 finding (F9): map the MLEADER's OWN `blockAttributeReferences[]`
        // overrides into the synthetic INSERT's `attributes[]` (see `wcsBoundsMath.ts::mapBlockAttributeReferences`'s
        // doc) — previously always `[]`, so an override was invisible to `resolveAttributeContribution`
        // and bounds silently fell back to the block definition's default ATTDEF footprint.
        // @codex T1 arm(c) round-24 P2 finding: own-property-safe lookup (`safeRecordLookup`,
        // `wcsBoundsBlock.ts`) — a bare `ctx.blockDefinitions[entity.blockName]` index falls
        // through to an inherited `Object.prototype` member for a block name like `"constructor"`,
        // which `mapBlockAttributeReferences` would then misread as a `BlockDefinition`.
        attributes: mapBlockAttributeReferences(entity.blockAttributeReferences, safeRecordLookup(ctx.blockDefinitions, entity.blockName)),
        customProperties: {},
        annotationScales: [],
        annotativeContexts: [],
      } as unknown as Parameters<typeof blockInsertWcsBounds>[0],
      ctx,
    );
    if (!content) return null;
    box = box ? unionAabb3(box, content) : content;
  }
  return box;
}

/**
 * Compute one entity's axis-aligned WCS bounds. `ctx.blockDefinitions` (optional) lets a
 * `Block` (INSERT) entity — or a `Multileader` with BLOCK content — resolve its referenced
 * definition's real footprint; without it a `Block` returns `null` (honest "unknown beyond the
 * insertion point", NEVER an insertion-point-only box — see this module's header).
 *
 * BY-CONSTRUCTION COMPLETENESS: the switch covers every `drawingEntitySchema` arm (the
 * `default` branch is an `assertNever`), mirroring `captureEntityGeometry.ts::projectGeometry`'s
 * own exhaustiveness gate — a new entity type added to `drawingEntitySchema` without a bounds
 * arm here is a tsc compile error.
 */
export function computeEntityWcsBounds(entity: DrawingEntity, ctx?: BlockLookupContext): WcsAabb3 | null {
  switch (entity.type) {
    case "Line": {
      const s = lineLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Point": {
      const s = pointLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Circle": {
      const s = circleLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Arc": {
      const s = arcLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Ellipse": {
      const s = ellipseLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Spline": {
      const s = splineLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "LWPOLYLINE": {
      const s = lwpolylineLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Text": {
      const s = textLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "MText": {
      const s = mtextLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Dimension": {
      // @codex T1 arm(c) round-22 P2 finding: resolve the entity's NAMED DIMSTYLE
      // (`entity.styleName`) against the threaded `ctx.dimensionStyles` table (falling back to
      // "Standard", see `resolveDimensionStyleFromContext`'s doc) — see `dimensionLocal`'s own
      // doc for the full gap this closes.
      const s = dimensionLocal(entity, resolveDimensionStyleFromContext(entity.styleName, ctx?.dimensionStyles));
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Multileader":
      return multileaderWcsBounds(entity, ctx);
    case "Revision Cloud": {
      const s = revisionCloudLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Wipeout": {
      const s = wipeoutLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Hatch": {
      const s = hatchLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Block":
      return blockInsertWcsBounds(entity, ctx);
    case "Rectangular Associative Array": {
      const s = arrayRectLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Viewport": {
      const s = viewportLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Polyline": {
      const s = heavyPolylineLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    case "Table":
      // Block-backed, exactly like `Block` above: the footprint of the
      // anonymous `*T<N>` graphics block this table references. Returns
      // `null` without a block table — honest absence, never an
      // insertion-point-only box.
      return tableWcsBounds(entity, ctx);
    case "OLE Frame": {
      const s = oleFrameLocal(entity);
      return aabb3FromLocalPoints(s.points, s.extrusion);
    }
    default: {
      const exhaustive: never = entity;
      throw new Error(`computeEntityWcsBounds: unhandled entity type ${String((exhaustive as { type?: string }).type)}`);
    }
  }
}
